import { useState } from "react";
import { MessageCircle, Info } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import { openWa } from "@/lib/wa";

export default function Tires() {
  const { t, pick } = useLang();
  const { brands } = useData();

  return (
    <main>
      <SEO title="الإطارات والمقاسات | العلامات العالمية المتوفرة لدى ميرو في الرياض" description="اختر الإطار المناسب لسيارتك: تعرف على علامات الإطارات العالمية المتوفرة لدى ميرو، وافهم مكونات مقاس الإطار مثل 205/55 R16، واستفسر عبر واتساب." path="/tires" />
      <PageHero eyebrow="TIRES & SIZES" title={t("tires.title")} sub={t("tires.sub")} image="https://images.unsplash.com/photo-1611633859589-7990d2fbb56b?q=85&w=1600&auto=format&fit=crop" />

      <TireSizeExplainer />

      <section className="py-20 sm:py-24" data-testid="tire-brands-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="eyebrow mb-3">BRANDS</p>
                <h2 className="text-2xl font-bold text-white sm:text-3xl">{t("tires.brandsTitle")}</h2>
              </div>
              <p className="max-w-md text-xs leading-6 text-dim">{t("tires.note")}</p>
            </div>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="brands-grid">
            {(brands || []).map((b, i) => (
              <Reveal key={b.slug} delay={i % 3}>
                <div className="group flex h-full flex-col rounded-3xl border border-white/10 bg-coal/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40" data-testid={`brand-card-${b.slug}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-xl font-extrabold text-white group-hover:text-gold">{pick(b.name_ar, b.name_en)}</h3>
                    <span dir="ltr" className="font-display text-sm font-bold uppercase tracking-widest text-dim">{b.name_en}</span>
                  </div>
                  <span className="mb-3 w-fit rounded-full bg-gold/10 px-3 py-1 text-xs font-bold text-gold">{pick(b.tag_ar, b.tag_en)}</span>
                  <p className="flex-1 text-sm leading-7 text-mist">{pick(b.blurb_ar, b.blurb_en)}</p>
                  <button
                    onClick={() => openWa(`السلام عليكم، أرغب في الاستفسار عن مقاسات إطارات ${b.name_ar} المتوفرة من ميرو.\nمقاس الإطار المطلوب: ____.\nنوع السيارة: ____.\nالحي: ____.`)}
                    className="mt-6 flex items-center justify-center gap-2 rounded-full border border-[#1FAF5A]/50 py-2.5 text-sm font-bold text-[#2ecc71] transition-all hover:bg-[#1FAF5A]/10"
                    data-testid={`brand-wa-${b.slug}`}
                  >
                    <MessageCircle size={15} /> {t("tires.brandCta")}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function TireSizeExplainer() {
  const { t, pick } = useLang();
  const [width, setWidth] = useState(205);
  const [ratio, setRatio] = useState(55);
  const [rim, setRim] = useState(16);

  const sizeStr = `${width}/${ratio} R${rim}`;
  const scale = width / 205;
  const wall = Math.max(24, Math.min(90, ratio * 1.1));

  return (
    <section className="border-y border-white/5 bg-coal/40 py-20 sm:py-24" data-testid="tire-size-explainer">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="eyebrow mb-3">SIZE DECODER</p>
          <h2 className="text-2xl font-bold text-white sm:text-3xl">{t("tires.explainerTitle")}</h2>
          <p className="mt-3 max-w-xl text-sm leading-7 text-mist">{t("tires.explainerSub")}</p>
        </Reveal>

        <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: t("tires.width"), val: width, set: setWidth, min: 155, max: 355, desc: t("tires.widthDesc") },
                { label: t("tires.ratio"), val: ratio, set: setRatio, min: 25, max: 80, desc: t("tires.ratioDesc") },
                { label: t("tires.rim"), val: rim, set: setRim, min: 12, max: 24, desc: t("tires.rimDesc") },
              ].map((f, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-ink/70 p-4 text-center" data-testid={`size-field-${i}`}>
                  <label className="mb-2 block text-xs font-bold text-gold">{f.label}</label>
                  <input
                    type="number"
                    min={f.min}
                    max={f.max}
                    value={f.val}
                    onChange={(e) => f.set(Math.max(f.min, Math.min(f.max, Number(e.target.value) || f.min)))}
                    className="field !px-2 text-center font-display text-xl font-bold"
                    dir="ltr"
                    data-testid={`size-input-${i}`}
                  />
                  <p className="mt-3 hidden text-[11px] leading-5 text-dim sm:block">{f.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-center gap-4 rounded-2xl border border-gold/25 bg-ink/70 px-6 py-4">
              <span className="font-display text-3xl font-extrabold tracking-widest text-gold" dir="ltr" data-testid="size-preview">{sizeStr}</span>
            </div>
            <button
              onClick={() => openWa(`السلام عليكم، أرغب في الاستفسار عن توفر إطار بمقاس ${sizeStr} من ميرو.\nنوع السيارة: ____.\nالحي: ____.`)}
              className="btn-wa mt-6 w-full"
              data-testid="size-enquiry-button"
            >
              <MessageCircle size={17} /> {t("tires.enquiry")} — {sizeStr}
            </button>
            <p className="mt-4 flex items-start gap-2 text-[11px] leading-5 text-dim">
              <Info size={13} className="mt-0.5 shrink-0" /> {t("tires.note")}
            </p>
          </Reveal>

          <Reveal delay={1} className="flex justify-center">
            <div className="relative">
              <svg width="340" height="340" viewBox="0 0 340 340" dir="ltr" data-testid="size-visual">
                <circle cx="170" cy="170" r="160" fill="#0b0f16" stroke="#FFC400" strokeWidth="2" strokeOpacity="0.4" />
                <circle cx="170" cy="170" r={160 - wall * scale} fill="none" stroke="#FFC400" strokeWidth="1" strokeDasharray="4 6" strokeOpacity="0.5" />
                <circle cx="170" cy="170" r={rim * 4.2} fill="#121a26" stroke="#FFC400" strokeWidth="2" />
                <circle cx="170" cy="170" r={rim * 2.2} fill="#080B10" />
                {Array.from({ length: 10 }).map((_, i) => (
                  <rect key={i} x="166" y="14" width="8" height="18" rx="4" fill="#FFC400" opacity={i % 2 ? 0.4 : 0.9} transform={`rotate(${i * 36} 170 170)`} />
                ))}
                <text x="170" y="90" textAnchor="middle" fill="#FFC400" fontSize="15" fontWeight="700">{width}mm</text>
                <text x="170" y="196" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">R{rim}</text>
              </svg>
              <p className="mt-4 text-center text-sm text-mist">{pick("جرّب تغيير الأرقام وراقب شكل الإطار يتغير", "Change the numbers and watch the tire shape change")}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
